/**
 * PLATE IMPORT PAGE
 * 
 * Temporary page to import plate tags from CSV.
 * Add to your routes, run the import, then remove.
 * 
 * Route suggestion: /import-plates
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, Check, AlertCircle, Loader2 } from 'lucide-react';
import { parseCSV, importPlateData, previewImport } from '../utils/importPlateTags';

type ImportStage = 'upload' | 'preview' | 'importing' | 'complete';

export default function ImportPlates() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [stage, setStage] = useState<ImportStage>('upload');
  const [csvData, setCsvData] = useState<ReturnType<typeof parseCSV> | null>(null);
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof previewImport>> | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [result, setResult] = useState<{
    matched: number;
    updated: number;
    notFound: string[];
    errors: string[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setError(null);
    
    try {
      const text = await file.text();
      const parsed = parseCSV(text);
      
      if (parsed.length === 0) {
        setError('No valid plate data found in CSV');
        return;
      }
      
      setCsvData(parsed);
      
      // Run preview
      setStage('preview');
      const previewResult = await previewImport(parsed);
      setPreview(previewResult);
      
    } catch (err) {
      console.error('Error parsing CSV:', err);
      setError('Failed to parse CSV file. Please check the format.');
    }
  };

  const handleImport = async () => {
    if (!csvData) return;
    
    setStage('importing');
    setError(null);
    
    try {
      const importResult = await importPlateData(csvData, (current, total, message) => {
        setProgress({ current, total, message });
      });
      
      setResult(importResult);
      setStage('complete');
      
    } catch (err) {
      console.error('Import error:', err);
      setError(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setStage('preview');
    }
  };

  const handleReset = () => {
    setStage('upload');
    setCsvData(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-2 text-on-bg transition-default font-body"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        {/* Header */}
        <div className="glass-card p-6 mb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-500 mb-4">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-glass mb-2">Import Plate Tags</h1>
          <p className="text-glass-secondary">
            Import tags, barcodes, and storage locations from your CSV file
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="glass-card p-4 mb-6 border-l-4 border-red-500 bg-red-500/10">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Upload Stage */}
        {stage === 'upload' && (
          <div className="glass-card p-8">
            <div 
              className="border-2 border-dashed border-slate-500 rounded-xl p-8 text-center hover:border-indigo-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-12 h-12 text-glass-muted mx-auto mb-4" />
              <p className="text-glass mb-2">Click to select your CSV file</p>
              <p className="text-glass-muted text-sm">
                Expected columns: Plate Name, Manufacturer, Barcode, Storage Location, Tags
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}

        {/* Preview Stage */}
        {stage === 'preview' && preview && (
          <div className="space-y-4">
            {/* Stats */}
            <div className="glass-card p-6">
              <h2 className="text-lg font-bold text-glass mb-4">Import Preview</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-green-500/20 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-green-400">{preview.wouldMatch}</div>
                  <div className="text-green-300 text-sm">Plates Found</div>
                </div>
                <div className="bg-indigo-500/20 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-indigo-400">{preview.wouldUpdate}</div>
                  <div className="text-indigo-300 text-sm">Will Be Updated</div>
                </div>
              </div>

              {preview.notFound.length > 0 && (
                <div className="bg-amber-500/10 rounded-lg p-4 mb-4">
                  <div className="text-amber-400 font-medium mb-2">
                    {preview.notFound.length} plates not found in your collection
                  </div>
                  <div className="max-h-32 overflow-y-auto text-sm text-amber-300/70">
                    {preview.notFound.slice(0, 20).map((name, i) => (
                      <div key={i}>{name}</div>
                    ))}
                    {preview.notFound.length > 20 && (
                      <div className="italic">...and {preview.notFound.length - 20} more</div>
                    )}
                  </div>
                </div>
              )}

              {preview.sampleUpdates.length > 0 && (
                <div>
                  <div className="text-glass-secondary text-sm mb-2">Sample updates:</div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {preview.sampleUpdates.map((item, i) => (
                      <div key={i} className="bg-slate-700/50 rounded-lg p-3 text-sm">
                        <div className="font-medium text-glass">{item.brand} - {item.name}</div>
                        <div className="text-glass-muted">{item.updates.join(', ')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleReset}
                className="flex-1 px-6 py-3 border-2 border-slate-600 text-glass-secondary rounded-xl font-semibold hover:bg-slate-800/40 transition-default"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={preview.wouldUpdate === 0}
                className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-default disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                Import {preview.wouldUpdate} Plates
              </button>
            </div>
          </div>
        )}

        {/* Importing Stage */}
        {stage === 'importing' && (
          <div className="glass-card p-8 text-center">
            <Loader2 className="w-12 h-12 text-indigo-400 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-glass mb-2">Importing...</h2>
            <p className="text-glass-secondary mb-4">{progress.message}</p>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div 
                className="bg-indigo-500 h-2 rounded-full transition-all"
                style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
              />
            </div>
            <p className="text-glass-muted text-sm mt-2">
              {progress.current} / {progress.total}
            </p>
          </div>
        )}

        {/* Complete Stage */}
        {stage === 'complete' && result && (
          <div className="space-y-4">
            <div className="glass-card p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500 mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-glass mb-2">Import Complete!</h2>
              <p className="text-glass-secondary">
                Successfully updated {result.updated} plates with tags and data.
              </p>
            </div>

            <div className="glass-card p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400">{result.matched}</div>
                  <div className="text-glass-muted text-sm">Matched</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-400">{result.updated}</div>
                  <div className="text-glass-muted text-sm">Updated</div>
                </div>
              </div>

              {result.notFound.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-600">
                  <div className="text-amber-400 text-sm">
                    {result.notFound.length} plates from CSV weren't found in your collection
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => navigate('/collection?type=plate')}
                className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-default"
              >
                View My Plates
              </button>
              <button
                onClick={handleReset}
                className="px-6 py-3 border-2 border-slate-600 text-glass-secondary rounded-xl font-semibold hover:bg-slate-800/40 transition-default"
              >
                Import Another
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
